import { CreateComment, ICommentService } from "@iventis/comments/src/comments-service";
import { assetsApi, commentsApi, permissionsApi } from "@iventis/api/src/api";
import { Comment } from "@iventis/domain-model/model/comment";
import { Asset } from "@iventis/domain-model/model/asset";
import { User } from "@iventis/domain-model/model/user";
import { CreateAssetRequest } from "@iventis/domain-model/model/createAssetRequest";
import { AssetType } from "@iventis/domain-model/model/assetType";
import { NewCommentImage } from "@iventis/comments/src/comment-types";
import { getAssetSignature } from "@iventis/utilities";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";

export const commentsApiFunctions: Pick<
    ICommentService,
    "get" | "getByResourceId" | "delete" | "update" | "create" | "getUser" | "getUserProfilePicture" | "getCommentImage" | "postCommentImage"
> = {
    get: async (commentId: string, resourceId: string) => {
        const response = await commentsApi.get<Comment>(`resources/${resourceId}/comments/${commentId}`, { suppressErrors: true });
        return response?.data;
    },
    getByResourceId: async (resourceId: string) => {
        const response = await commentsApi.get<Comment[]>(`resources/${resourceId}/comments`);
        return response.data;
    },
    delete: async (commentId: string, resourceId: string) =>
        commentsApi.delete(`resources/${resourceId}/comments/${commentId}`, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.comments.comment) }),
                message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.comments.comment) }),
            },
        }),
    update: async (comment: Comment, resourceId: string) => {
        await commentsApi.patch(`resources/${resourceId}/comments/${comment.id}`, comment, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.comments.comment) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.comments.comment) }),
            },
        });
    },
    create: async (comment: CreateComment, resourceId: string) => {
        await commentsApi.post(`resources/${resourceId}/comments`, comment, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.comments.comment) }),
                message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.comments.comment) }),
            },
        });
    },
    getUser: async (userId: string) => {
        const response = await permissionsApi.get<User>(`users/${userId}`);
        return response.data;
    },
    getUserProfilePicture: async (userId: string) => {
        const asset = await assetsApi.get<Asset>(`assets/${userId}`);
        return getAssetSignature(asset.data.assetUrl, asset.data.authoritySignature);
    },
    getCommentImage: async (imageId: string) => {
        const { data: asset } = await assetsApi.get<Asset>(`assets/${imageId}`);
        return { url: getAssetSignature(asset?.assetUrl, asset?.authoritySignature), name: asset?.name };
    },
    postCommentImage: async (assetId: string, commentImage: NewCommentImage) => {
        const requestBody: Partial<CreateAssetRequest> = { dataUrl: commentImage.base64Image, id: assetId, type: AssetType.CommentImage, name: commentImage.imageName };
        const { data: asset } = await assetsApi.post(`assets?height=${commentImage.height}&width=${commentImage.width}`, requestBody, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.map8.fileUpload.image_comment) }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.map8.fileUpload.image_comment) }),
            },
        });
        return asset;
    },
};
